import React, {
  Fragment,
  useContext,
  useState,
  lazy,
  Suspense,
  useEffect,
  useRef,
  startTransition
} from "react";
import socketIOClient from "socket.io-client";

import profileService from "../services/profile.service";
// Aliased: the component also has a `latestPosts` state array, and a local const
// shadows the import — `latestPosts()` then throws "not a function" inside the
// fetch's try/catch, silently killing both this widget and the birthday posts.
import { callProc, latestPosts as fetchLatestPosts } from "../services/data.service";
import Load from "../components/Load";
import { AuthContext } from "../context/authContext";
import { Link, useNavigate } from 'react-router-dom';
import Flash from "../components/Flash";
import { Helmet } from 'react-helmet';

// Lazy loaded components
const Hallfame = lazy(() => import("../components/Hallfame"));
const ChatDemo = lazy(() => import("../components/chat/ChatDemo"));
const BrainDrainWidget = lazy(() => import("../components/BrainDrainWidget"));
const LyricsGenerator = lazy(() => import("../components/LyricsGenerator"));

// Pinned hero slot. Deliberately hand-set rather than derived from the feed: the
// feed already holds 11k+ user-posted YouTube links, so "newest Youtube post"
// would surface a random one. Update videoId/title when a new original ships.
// Bots are flagged users.lead = 9. sp_getPosts does not filter them, so the feed
// widget queries directly.
// The latest-posts query moved server-side to GET /data/latest-posts — it was
// the last raw SELECT this page posted to /profile/getResult.

// Pinned originals, newest first. Hand-set rather than derived from the feed —
// there are 11k+ user-posted YouTube links, so "newest Youtube post" would
// surface a random one. Add a new song at the top and drop the oldest.
const FEATURED = [
  {
    // Added 2026-10-13; 'चाँद सी तू' (hGQE6iZmdJg) dropped as the oldest.
    videoId: 'udFzV_g9_DI',
    title: 'जय जय माँ अम्बे (Jai Jai Maa Ambe)',
    subtitle: 'दुर्गा पूजा विशेष भजन · Navratri 2026',
  },
  {
    // Added 2026-08-10; 'तिरंगा उठेगा' (yVG5qbU5ZTY) dropped as the oldest.
    videoId: 'sAsAExujTgY',
    title: 'Move It All Night 💃',
    subtitle: 'हिंग्लिश डांस पार्टी सॉन्ग',
  },
];
const FEATURED_BADGE = 'Our Production';
const Createpost = lazy(() => import("../components/Createpost"));
const GameWidget = lazy(() => import("../components/GameWidget"));
const Youtube = lazy(() => import("../components/Youtube"));

export default function Home() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showCreatePost, setShowCreatePost] = useState(false);

  const cdn = 'https://files.geetsuhane.com/';
  const [bday, setBday] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const [postLikes, setPostLikes] = useState({});
  const [voiceRoomInfo, setVoiceRoomInfo] = useState({ participantCount: 0, participants: [] });
  const voiceSocket = useRef(null);
  const [postid, setPostId] = useState(null);
  // Games open by default everywhere; the mobile toggle stays so they can be
  // collapsed (they are ~1,700px of page). Desktop rail is always open via CSS.
  const [gamesOpen, setGamesOpen] = useState(true);


// Function to fetch birthday posts
const getbday = async() => {
  setBday(await callProc('sp_getPosts', [0, 0, 0, 3]));
};

// Fetch data in parallel on mount
useEffect(() => {
  const fetchData = async () => {
    try {
      // Both of these used to be SQL built in the browser and posted to
      // /profile/getResult. They now go through /data, where the procedure is
      // allow-listed and the arguments are bound.
      const [bdayRows, postRows] = await Promise.all([
        callProc('sp_getPosts', [0, 0, 0, 3]),
        fetchLatestPosts(),
      ]);
      setBday(bdayRows || []);
      // getResult returns [[rows]] for CALL but [rows] for a plain SELECT.
      // LATEST_POSTS_QRY is a SELECT, so unwrap defensively or .length is undefined
      // and the widget silently renders nothing.
      // One post per person. A single chatty member could otherwise fill the
      // whole widget; rows arrive newest-first, so the first hit per user is
      // their latest. Deduping here rather than with a GROUP BY keeps the query
      // off a full scan of 70k+ posts.
      const rows = postRows;
      const seen = new Set();
      const oncePerUser = rows.filter((row) => {
        const key = row?.userId || row?.SID;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setLatestPosts(oncePerUser);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };
  fetchData();
}, []);



// Handle like action for posts
const handleLikePost = async (postId) => {
  if (!currentUser) {
    navigate('/login');
    return;
  }
  try {
    const ret = await profileService.getResult({
      qry: `CALL sp_updateLikes('${currentUser?.id}', ${postId})`
    });
    if (ret?.[0][0]?.res === 1) {
      setPostLikes(prev => ({
        ...prev,
        [postId]: (prev[postId] || 0) + 1
      }));
    }
  } catch (err) {
    console.error('Like error:', err);
  }
};

// Connect to voice room socket to get participant count
useEffect(() => {
  // Skip socket in in-app browsers as they have WebSocket issues
  const ua = navigator.userAgent || '';
  const isInAppBrowser = /FBAN|FBAV|Instagram|LinkedIn|Snapchat|Twitter|TikTok/i.test(ua);
  if (isInAppBrowser) return;

  try {
    const ENDPOINT = process.env.REACT_APP_API_END_POINT;
    if (!ENDPOINT) {
      console.warn('No API endpoint configured');
      return;
    }

    voiceSocket.current = socketIOClient(ENDPOINT, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 2000
    });

    voiceSocket.current.on('connect', () => {
      voiceSocket.current.emit('get-voice-room-status');
    });

    voiceSocket.current.on('connect_error', (err) => {
      console.warn('Socket connection error:', err.message);
    });

    // Helper to process voice room data
    const processVoiceRoomData = (data) => {
      if (!data) return;
      const { hostSeat, seats, listeners } = data;

      const allParticipants = [];
      if (hostSeat) {
        allParticipants.push({ name: hostSeat.name, picture: hostSeat.picture, isHost: true });
      }
      seats?.forEach(seat => {
        if (seat) {
          allParticipants.push({ name: seat.name, picture: seat.picture, isSpeaker: true });
        }
      });
      listeners?.forEach(listener => {
        if (listener) {
          allParticipants.push({ name: listener.name, picture: listener.picture, isListener: true });
        }
      });

      setVoiceRoomInfo({
        participantCount: allParticipants.length,
        participants: allParticipants.slice(0, 3)
      });
    };

    voiceSocket.current.on('voice-room-status', processVoiceRoomData);
    voiceSocket.current.on('voice-room-changed', processVoiceRoomData);

    return () => {
      voiceSocket.current?.disconnect();
    };
  } catch (err) {
    console.error('Socket setup error:', err);
  }
}, []);


const postClicked = (pid) => {
  startTransition(() => {
    setPostId(pid);
  });
  //console.log(pid);
};

// Handle denied users
useEffect(() => {
  if (currentUser?.status === 2) {
    navigate("/denied");
  }
}, [currentUser?.status, navigate]);


  return (
    <Fragment>
      <Helmet>
        <title>Geet Suhane - Indian Music & Entertainment Community</title>
        <meta name="description" content="Join Geet Suhane - India's premier music community. Enjoy live voice rooms, AI lyrics generator, Brain Drain games, and connect with music lovers." />
        <meta name="keywords" content="Indian music, Hindi songs, live voice chat, AI lyrics, music community, Bollywood, karaoke, singing" />
        <meta property="og:title" content="Geet Suhane - Indian Music Community" />
        <meta property="og:description" content="Join India's premier music community. Live voice rooms, AI lyrics, games & more!" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Geet Suhane - Indian Music Community" />
        <meta name="twitter:description" content="Join India's premier music community. Live voice rooms, AI lyrics, games & more!" />
      </Helmet>
      <div className="main-content right-chat-active" style={{ paddingTop: '65px' }}>
        <div className="middle-sidebar-bottom" style={{ paddingTop: '0', marginTop: '0' }}>
          <div className="middle-sidebar-left" style={{ paddingTop: '0', marginTop: '0' }}>
            <div className="row feed-body" style={{ marginTop: '0' }}>
              <div className="col-xl-8 col-xxl-9 col-lg-8" style={{ paddingTop: '0' }}>

                  <div style={{ margin: '0', padding: '0' }}>

                    <div style={{ margin: '0', padding: '0' }}>
                        {/* A signed-out visitor previously saw no statement of what
                            this site is and no invitation to join — just avatars,
                            scores and other people's game leaderboards. The member
                            count is read from the DB, not invented. */}
                        {!currentUser && (
                          <div className="gs-hero">
                            <h1 className="gs-hero__title">Sing Hindi songs, together</h1>
                            <p className="gs-hero__sub">
                              GeetSuhane is a community of <strong>2,800+ singers</strong>. Record
                              over our original songs, join a live voice room, play with the
                              others — all free.
                            </p>
                            <div className="gs-hero__actions">
                              <Link to="/register" className="gs-hero__join">Join free</Link>
                              <Link to="/login" className="gs-hero__signin">I have an account</Link>
                            </div>
                          </div>
                        )}

                    <Suspense fallback={<Load />}>
                    <Flash />
                    </Suspense>
                         <div className="overflow-hidden" style={{ width: '100%', paddingTop: '4px' }}>
                        <Suspense fallback={<Load />}>
                        <Hallfame></Hallfame>
                        </Suspense>
                          </div>

                        {/* Karaoke entry point. Animation is all CSS (.gs-cta in
                            customStyles.scss) — the only asset is a 31KB backdrop,
                            because the home page was just cut from 47MB to 4.3MB. */}
                        <Link
                          to="/fs"
                          className="gs-cta"
                          style={{ '--gs-cta-image': `url(${cdn}home/sing-cta.jpg)` }}
                        >
                          <span className="gs-cta__row">
                            <span className="gs-cta__mic">🎤</span>
                            <span className="gs-cta__text">
                              <span className="gs-cta__kicker">Sing an original</span>
                              <span className="gs-cta__title">Your voice, our songs</span>
                              <span className="gs-cta__sub">
                                Backing track, scrolling lyrics, studio effects — walk away with
                                a video for your own channel.
                              </span>
                            </span>
                            <span className="gs-cta__eq" aria-hidden="true">
                              <i /><i /><i /><i /><i />
                            </span>
                          </span>
                          <span className="gs-cta__go">
                            <span>Start singing — it's free</span>
                            <span>→</span>
                          </span>
                        </Link>

                        {/* Daily Tasks Banner */}
                        <Link to="/userpage" style={{ textDecoration: 'none' }}>
                          <div style={{
                            marginBottom: '15px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                          }}>
                            {/* Was an 8.95MB animated GIF — a third of the whole
                                home page. Same animation as h264 is 373KB. */}
                            <video
                              src={cdn + 'home/task_720.mp4'}
                              autoPlay
                              muted
                              loop
                              playsInline
                              preload="metadata"
                              aria-label="Daily Tasks"
                              style={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                          </div>
                        </Link>

                        {/* GS Conference Room Display */}
                        <div className="card d-block border-0 shadow-xss rounded-xxl mb-3 overflow-hidden">
                          <div
                            className="p-4"
                            style={{
                              background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
                              position: 'relative'
                            }}
                          >
                            {/* Header with mic icon and participant count */}
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div className="d-flex align-items-center">
                                <div
                                  className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    background: 'linear-gradient(135deg, #ff416c, #ff4b2b)',
                                    boxShadow: '0 4px 15px rgba(255, 65, 108, 0.4)'
                                  }}
                                >
                                  <i className="feather-mic text-white" style={{ fontSize: '22px' }}></i>
                                </div>
                                <div>
                                  <h3 className="fw-700 text-white mb-0" style={{ fontSize: '18px' }}>GS Conference Room</h3>
                                  <p className="mb-0" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                                    Live voice chat
                                  </p>
                                </div>
                              </div>
                              {/* Participant count badge */}
                              <div
                                className="d-flex align-items-center"
                                style={{
                                  background: voiceRoomInfo.participantCount > 0 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255,255,255,0.1)',
                                  padding: '6px 12px',
                                  borderRadius: '20px',
                                  border: voiceRoomInfo.participantCount > 0 ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid rgba(255,255,255,0.2)'
                                }}
                              >
                                <i
                                  className="feather-users me-2"
                                  style={{ fontSize: '14px', color: voiceRoomInfo.participantCount > 0 ? '#22c55e' : 'rgba(255,255,255,0.6)' }}
                                ></i>
                                <span
                                  className="fw-600"
                                  style={{ fontSize: '14px', color: voiceRoomInfo.participantCount > 0 ? '#22c55e' : 'rgba(255,255,255,0.6)' }}
                                >
                                  {voiceRoomInfo.participantCount}
                                </span>
                              </div>
                            </div>

                            {/* Participant avatars (if any) */}
                            {voiceRoomInfo.participants.length > 0 && (
                              <div className="d-flex align-items-center mb-3">
                                <div className="d-flex" style={{ marginLeft: '8px' }}>
                                  {voiceRoomInfo.participants.map((participant, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        marginLeft: idx > 0 ? '-12px' : '0',
                                        position: 'relative',
                                        zIndex: 3 - idx
                                      }}
                                    >
                                      <img
                                        src={
                                          participant.picture
                                            ? (participant.picture.includes('googleusercontent') || participant.picture.startsWith('http')
                                              ? participant.picture
                                              : participant.picture.startsWith('gsb/')
                                                ? cdn + participant.picture
                                                : cdn + 'users/' + participant.picture)
                                            : cdn + 'users/default-avatar.png'
                                        }
                                        alt={participant.name}
                                        style={{
                                          width: '36px',
                                          height: '36px',
                                          borderRadius: '50%',
                                          border: participant.isHost ? '2px solid #ffd700' : '2px solid #302b63',
                                          objectFit: 'cover'
                                        }}
                                      />
                                      {participant.isHost && (
                                        <span
                                          style={{
                                            position: 'absolute',
                                            top: '-6px',
                                            right: '-2px',
                                            fontSize: '12px'
                                          }}
                                        >👑</span>
                                      )}
                                    </div>
                                  ))}
                                  {voiceRoomInfo.participantCount > 3 && (
                                    <div
                                      style={{
                                        marginLeft: '-12px',
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.2)',
                                        border: '2px solid #302b63',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: 'white'
                                      }}
                                    >
                                      +{voiceRoomInfo.participantCount - 3}
                                    </div>
                                  )}
                                </div>
                                <span
                                  className="ms-2"
                                  style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}
                                >
                                  {voiceRoomInfo.participantCount === 1
                                    ? `${voiceRoomInfo.participants[0]?.name?.split(' ')[0]} is live`
                                    : `${voiceRoomInfo.participants[0]?.name?.split(' ')[0]} and ${voiceRoomInfo.participantCount - 1} others`
                                  }
                                </span>
                              </div>
                            )}

                            {/* Empty state */}
                            {voiceRoomInfo.participants.length === 0 && (
                              <p className="text-center mb-3" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                                No one is in the room yet. Be the first to join!
                              </p>
                            )}

                            {/* Join button */}
                            <div className="text-center">
                              <Link
                                to="/room"
                                className="btn w-100 rounded-xl text-white fw-600 py-2"
                                style={{
                                  background: voiceRoomInfo.participantCount > 0
                                    ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                                    : 'linear-gradient(135deg, #ff416c, #ff4b2b)',
                                  border: 'none',
                                  textDecoration: 'none',
                                  boxShadow: voiceRoomInfo.participantCount > 0
                                    ? '0 4px 15px rgba(34, 197, 94, 0.3)'
                                    : '0 4px 15px rgba(255, 65, 108, 0.3)'
                                }}
                              >
                                <i className={`feather-${voiceRoomInfo.participantCount > 0 ? 'headphones' : 'mic'} me-2`}></i>
                                {currentUser
                                  ? (voiceRoomInfo.participantCount > 0 ? 'Join Now' : 'Start Room')
                                  : 'Listen In'}
                              </Link>
                            </div>
                          </div>
                        </div>

                        {/* Mobile: content first, games demoted below the feed.
                            Desktop >=992px: two columns, games/birthdays in a sticky rail.
                            See .gs-home in components/customStyles.scss */}
                        <div className="gs-home">
                          <div className="gs-home__main">

                        {/* Lyrics Generator */}
                        <div>
                          <Suspense fallback={<Load />}>
                            <LyricsGenerator onPostCreated={getbday}></LyricsGenerator>
                          </Suspense>
                        </div>

                        {/* Beenga.in promo — sits directly above Featured. Static banner
                            hosted in Spaces (any swap needs a CDN flush, 7-day TTL). */}
                        <a
                          href="https://beenga.in"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'block', borderRadius: '12px', overflow: 'hidden',
                            marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                          }}
                        >
                          <img
                            src="https://gsfiles.sgp1.cdn.digitaloceanspaces.com/home/beenga-in.png"
                            alt="beenga.in — AI images & videos in seconds"
                            loading="lazy"
                            style={{
                              width: '100%', height: 'auto', aspectRatio: '1 / 1',
                              display: 'block', background: '#0b0b16'
                            }}
                          />
                        </a>

                        {/* Featured — pinned, deliberately outside the paged feed */}
                        <div style={{
                          background: '#fff', borderRadius: '12px', padding: '12px',
                          marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                            gap: '8px', marginBottom: '10px'
                          }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                              📌 Featured
                            </span>
                            <span style={{
                              fontSize: '11px', fontWeight: '700', color: '#fff',
                              background: 'linear-gradient(135deg, #ff416c, #ff4b2b)',
                              padding: '3px 10px', borderRadius: '999px',
                              letterSpacing: '0.3px', textTransform: 'uppercase'
                            }}>
                              🎵 {FEATURED_BADGE}
                            </span>
                          </div>

                          <div className="gs-featured__grid">
                            {FEATURED.map((song) => (
                              <div key={song.videoId}>
                                <a
                                  href={`https://www.youtube.com/watch?v=${song.videoId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ textDecoration: 'none', display: 'block', position: 'relative' }}
                                >
                                  <img
                                    src={`https://img.youtube.com/vi/${song.videoId}/hqdefault.jpg`}
                                    alt={song.title}
                                    loading="lazy"
                                    style={{
                                      width: '100%', aspectRatio: '16 / 9', objectFit: 'cover',
                                      borderRadius: '10px', display: 'block', background: '#eee'
                                    }}
                                  />
                                  <span style={{
                                    position: 'absolute', top: '50%', left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '56px', height: '40px', borderRadius: '10px',
                                    background: 'rgba(0,0,0,0.75)', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center'
                                  }}>
                                    <i className="feather-play" style={{ color: '#fff', fontSize: '20px' }}></i>
                                  </span>
                                </a>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginTop: '10px', lineHeight: '1.4' }}>
                                  {song.title}
                                </div>
                                <div style={{ fontSize: '12px', color: '#888', marginTop: '3px' }}>
                                  {song.subtitle}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Composer sits under the featured song. It originally lived inside
                            ChatDemo at 4,813px of a 5,553px page — below every game
                            widget — which is why it does not go back there. */}
                        {currentUser && (
                          <Suspense fallback={<Load />}>
                            <Youtube />
                          </Suspense>
                        )}

                        {/* Latest Posts — stacked, no carousel */}
                        {latestPosts?.length > 0 && (
                          <div style={{
                            background: '#fff', borderRadius: '12px', padding: '12px',
                            marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                          }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>
                              📝 Latest Posts
                            </div>

                            {latestPosts.slice(0, 5).map((item) => {
                              const avatarUrl = item.picture
                                ? (item.picture.includes('googleusercontent') || item.picture.startsWith('http')
                                    ? item.picture
                                    : item.picture.startsWith('gsb/') ? cdn + item.picture : cdn + 'users/' + item.picture)
                                : "";
                              const isImg = item.mediaType === 'img' && item.mediaUrl;
                              const imgUrl = isImg
                                ? (item.mediaUrl.startsWith('http') ? item.mediaUrl : cdn + 'users/' + item.mediaUrl)
                                : null;
                              const isYt = item.mediaType === 'Youtube' && item.mediaUrl;
                              const ytId = isYt ? String(item.mediaUrl).split(/[?&]/)[0] : null;
                              return (
                                <div key={item.id} style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <Link to={`/userpage?uid=${item.SID || item.userId}`}>
                                      <img
                                        src={avatarUrl || cdn + 'users/default.png'}
                                        alt=""
                                        loading="lazy"
                                        // Some older accounts point at objects that are no
                                        // longer in the bucket (bot avatars especially), which
                                        // renders a broken-image icon in the feed.
                                        onError={(e) => {
                                          if (e.currentTarget.dataset.fallback) return;
                                          e.currentTarget.dataset.fallback = '1';
                                          e.currentTarget.src = cdn + 'users/default.png';
                                        }}
                                        style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #eee' }}
                                      />
                                    </Link>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <Link to={`/userpage?uid=${item.SID || item.userId}`} style={{ textDecoration: 'none' }}>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{item.name}</div>
                                      </Link>
                                      <div style={{ fontSize: '11px', color: '#999' }}>
                                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}
                                      </div>
                                    </div>
                                  </div>

                                  {item.text && (
                                    <Link to={`/comments?&postId=${item.id}`} style={{ textDecoration: 'none' }}>
                                      <div style={{
                                        fontSize: '13px', color: '#333', lineHeight: '1.55',
                                        whiteSpace: 'pre-wrap', marginBottom: isImg || isYt ? '8px' : '0',
                                        display: '-webkit-box', WebkitLineClamp: 4,
                                        WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                      }}>
                                        {item.text}
                                      </div>
                                    </Link>
                                  )}

                                  {isYt && (
                                    <a href={`https://www.youtube.com/watch?v=${ytId}`} target="_blank" rel="noreferrer"
                                       style={{ display: 'block', position: 'relative' }}>
                                      <img
                                        src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                                        alt=""
                                        style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: '10px', display: 'block', background: '#eee' }}
                                      />
                                      <span style={{
                                        position: 'absolute', top: '50%', left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '48px', height: '34px', borderRadius: '8px',
                                        background: 'rgba(0,0,0,0.75)', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center'
                                      }}>
                                        <i className="feather-play" style={{ color: '#fff', fontSize: '17px' }}></i>
                                      </span>
                                    </a>
                                  )}

                                  {isImg && (
                                    <Link to={`/comments?&postId=${item.id}`}>
                                      <img src={imgUrl} alt="" style={{ width: '100%', maxHeight: '240px', objectFit: 'cover', borderRadius: '10px' }} />
                                    </Link>
                                  )}
                                </div>
                              );
                            })}

                            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '10px', textAlign: 'center' }}>
                              <Link to="/moments" style={{ fontSize: '13px', color: '#ff416c', textDecoration: 'none', fontWeight: '500' }}>
                                View all posts →
                              </Link>
                            </div>
                          </div>
                        )}

                        <style>{`
                          @keyframes fadeIn {
                            from { opacity: 0; transform: translateX(10px); }
                            to { opacity: 1; transform: translateX(0); }
                          }
                        `}</style>


                          </div>

                          <aside className="gs-home__rail">

                        {/* Birthdays — all of today's in one card, no carousel */}
                        {bday?.length > 0 && (
                          <div style={{
                            background: '#fff',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '15px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                          }}>
                            <div style={{
                              fontSize: '16px', fontWeight: '700', color: '#333',
                              marginBottom: '4px'
                            }}>
                              🎂 Birthday Celebrations
                            </div>
                            <div style={{ fontSize: '13px', color: '#888', marginBottom: '14px' }}>
                              {bday.length === 1
                                ? 'Wishing our star a wonderful day!'
                                : `Wishing all ${bday.length} of them a wonderful day!`}
                            </div>

                            <div style={{
                              display: 'flex', flexWrap: 'wrap', gap: '14px'
                            }}>
                              {bday.map((item) => {
                                const avatarUrl = item.picture
                                  ? (item.picture.includes('googleusercontent') || item.picture.startsWith('http')
                                      ? item.picture
                                      : item.picture.startsWith('gsb/') ? cdn + item.picture : cdn + 'users/' + item.picture)
                                  : "";
                                return (
                                  <Link
                                    key={item.id}
                                    to={`/comments?&postId=${item.id}`}
                                    style={{
                                      textDecoration: 'none', textAlign: 'center',
                                      width: '84px', display: 'block'
                                    }}
                                  >
                                    <img
                                      src={avatarUrl || cdn + 'users/default.png'}
                                      alt=""
                                      style={{
                                        width: '64px', height: '64px', borderRadius: '50%',
                                        objectFit: 'cover', border: '3px solid #ff416c',
                                        display: 'block', margin: '0 auto 6px'
                                      }}
                                    />
                                    <div style={{
                                      fontSize: '12px', fontWeight: '600', color: '#333',
                                      lineHeight: '1.3', overflow: 'hidden',
                                      display: '-webkit-box', WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical', wordBreak: 'break-word'
                                    }}>
                                      {item.name}
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div className="gs-games">
                          <button
                            type="button"
                            className="gs-games__toggle"
                            onClick={() => setGamesOpen(o => !o)}
                            aria-expanded={gamesOpen}
                          >
                            <span>🎮 Games &amp; Leaderboards</span>
                            <span className="gs-games__chev">{gamesOpen ? '▲' : '▼'}</span>
                          </button>
                          <div className={gamesOpen ? 'gs-games__body is-open' : 'gs-games__body'}>
                        {/* Antakshari is hidden until the Suno API is available —
                            the widget and its home-page entry point are removed rather
                            than the route, so /antakshari still works for testing and
                            this is one block to restore. */}

                        {/* Boat Race Widget */}
                        <Suspense fallback={<Load />}>
                          <GameWidget
                            theme="boatrace"
                            icon="🚤"
                            title="Boat Race"
                            subtitle="Race your planet to victory!"
                            link="/boatrace"
                            buttonText="Play"
                            leaderboardEndpoint="boatrace"
                            leaderboardLabel="Yesterday's Winners"
                            scoreLabel="pts"
                            footerText="Team up • Race daily • Win big!"
                            footerIcon="🌊"
                            pictureType="planet"
                          />
                        </Suspense>

                        {/* Chess Widget - no leaderboard, winners not stored in DB */}
                        <Suspense fallback={<Load />}>
                          <GameWidget
                            theme="chess"
                            icon="♟️"
                            title="Chess"
                            subtitle="Challenge players or AI!"
                            link="/chess"
                            buttonText="Play"
                            footerText="Strategic battles await!"
                            footerIcon="👑"
                          />
                        </Suspense>

                        {/* GS Planet Games Widget */}
                        <Suspense fallback={<Load />}>
                          <GameWidget
                            theme="planetgames"
                            icon="🎰"
                            title="GS Planet Games"
                            subtitle="Bet on planets & win!"
                            link="/game"
                            buttonText="Bet"
                            leaderboardEndpoint="planetgames"
                            leaderboardLabel="Top Winners"
                            scoreLabel="GSC"
                            footerText="New round every minute!"
                            footerIcon="💰"
                          />
                        </Suspense>

                        {/* GS Cafe Widget */}
                        <Suspense fallback={<Load />}>
                          <GameWidget
                            theme="cafe"
                            icon="☕"
                            title="GS Cafe"
                            subtitle="Hang out with friends!"
                            link="/cafe"
                            buttonText="Visit"
                            leaderboardEndpoint="cafe"
                            leaderboardLabel="Top Players"
                            scoreLabel="pts"
                            footerText="Chat, relax & have fun!"
                            footerIcon="🍪"
                          />
                        </Suspense>

                        {/* Dream World Widget */}
                        <Suspense fallback={<Load />}>
                          <GameWidget
                            theme="dreamworld"
                            icon="🏆"
                            title="Dream World"
                            subtitle="Fantasy league for GS stars!"
                            link="/dw"
                            buttonText="Play"
                            leaderboardEndpoint="dreamworld"
                            leaderboardLabel="Top Earners"
                            scoreLabel="GSC"
                            footerText="Sign members • Build your dream team!"
                            footerIcon="⭐"
                          />
                        </Suspense>

                        {/* Brain Drain Widget */}
                        <Suspense fallback={<Load />}>
                          <BrainDrainWidget />
                        </Suspense>

                          </div>
                        </div>

                          </aside>
                        </div>

                        <div    className="card d-block border-0  rounded-xxxl pt-1">

                     <div id='survey'>
                       <Suspense fallback={<Load />}>
                         <ChatDemo></ChatDemo>
                       </Suspense>
                     </div>
  
                     
                </div>
               
     
               
         
          </div>
       
                </div>

             
             
              </div>

          </div> 
          
            </div>
            
          </div>
        </div>

        {/* Floating Create Post Button */}
        {currentUser && (
          <div
            onClick={() => setShowCreatePost(true)}
            style={{
              position: 'fixed',
              bottom: '80px',
              right: '20px',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ff416c, #ff4b2b)',
              boxShadow: '0 4px 15px rgba(255, 65, 108, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 1000,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 65, 108, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 65, 108, 0.4)';
            }}
          >
            <i className="feather-plus text-white" style={{ fontSize: '24px' }}></i>
          </div>
        )}

        {/* Create Post Modal */}
        {showCreatePost && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 1001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowCreatePost(false);
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '80vh',
                overflow: 'auto',
                position: 'relative'
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px 20px',
                  borderBottom: '1px solid #eee',
                  position: 'sticky',
                  top: 0,
                  background: 'white',
                  zIndex: 1
                }}
              >
                <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Create Post</h4>
                <div
                  onClick={() => setShowCreatePost(false)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <i className="feather-x" style={{ fontSize: '18px' }}></i>
                </div>
              </div>
              {/* Modal Body */}
              <div style={{ padding: '20px' }}>
                <Suspense fallback={<Load />}>
                  <Createpost />
                </Suspense>
              </div>
            </div>
          </div>
        )}

    </Fragment>
  );
}
